import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Table } from "antd";
import { NotificationManager } from "react-notifications";
import KycService from "../../services/kyc-service";
import { KYC_DEFAULT_FILTER_PAYLOAD } from "./kycDesignShared";
import { formatKycCreatedOnDate, getKycAppliedFilterPayload } from "./kycHelpers";

const getSortOrder = (tableSorter, columnKey) => {
	if (tableSorter.field !== columnKey) {
		return null;
	}
	return tableSorter.order === "ASC" ? "ascend" : tableSorter.order === "DESC" ? "descend" : null;
};

const getCellValue = (record, keys) => {
	for (const key of keys) {
		const value = record?.[key];
		if (value !== undefined && value !== null && value !== "") {
			return value;
		}
	}
	return "-";
};

const formatRecordCount = (total, range) => {
	if (!total) {
		return "Showing 0 records";
	}
	return `Showing ${range[0]}-${range[1]} of ${total.toLocaleString()} records`;
};

const parseCompanyListResponse = (response, fallbackPage = 1) => {
	const success = response?.data?.statusCode === 200 || response?.data?.status === true;
	if (!success) {
		return { rows: [], totalRecords: 0, currentPage: fallbackPage };
	}

	const responseData = response.data.responseData || {};
	const accountList = Array.isArray(responseData)
		? responseData
		: responseData.kycCompanyLists ||
			responseData.kycGroupCompanyLists ||
			responseData.companyListing ||
			responseData.companyList ||
			responseData.data ||
			[];
	const rows = Array.isArray(accountList) ? accountList : [];
	const totalRecords = Number(responseData.totalRecords || rows.length || 0);
	const parsedPage = Number(responseData.currentPage);
	const currentPage =
		responseData.currentPage !== undefined &&
		responseData.currentPage !== null &&
		responseData.currentPage !== "" &&
		!Number.isNaN(parsedPage) &&
		parsedPage > 0
			? parsedPage
			: fallbackPage;

	return { rows, totalRecords, currentPage };
};

const KycMaterialCustomerTab = ({
	productName,
	search = "",
	isActive,
	navigationContext = {},
}) => {
	const [isLoading, setIsLoading] = useState(false);
	const [customerTable, setCustomerTable] = useState({
		rows: [],
		totalRecords: 0,
		currentPage: 1,
		pageSize: 10,
	});
	const [tableSorter, setTableSorter] = useState({
		field: "CreatedOn",
		order: "DESC",
	});

	const isplCustomer = String(
		navigationContext.materialsCustomer || navigationContext.accountName || ""
	).trim();

	const loadCustomerList = useCallback(async () => {
		if (!isplCustomer && !productName) {
			setCustomerTable((prev) => ({ ...prev, rows: [], totalRecords: 0 }));
			return;
		}

		setIsLoading(true);
		try {
			const requestData = {
				pageindex: customerTable.currentPage,
				pagesize: customerTable.pageSize,
				sortcolumn: tableSorter.field,
				orderBy: tableSorter.order,
				search: String(search || "").trim(),
				...KYC_DEFAULT_FILTER_PAYLOAD,
				...getKycAppliedFilterPayload(),
				ispl_customer: isplCustomer,
				ispl_product: String(productName || "").trim(),
			};
			const groupCompanyId = String(navigationContext.groupCompanyId || "").trim();
			if (groupCompanyId) {
				requestData.groupcomanyId = groupCompanyId;
			}

			const response = await KycService.getKycCompanyListAPI(requestData);
			const result = parseCompanyListResponse(response, customerTable.currentPage);
			setCustomerTable((prev) => ({
				...prev,
				rows: result.rows,
				totalRecords: result.totalRecords,
				currentPage: result.currentPage,
			}));
		} catch (error) {
			console.log(error, "Error");
			NotificationManager.error("Unable to load customer listing.");
			setCustomerTable((prev) => ({ ...prev, rows: [], totalRecords: 0 }));
		} finally {
			setIsLoading(false);
		}
	}, [
		isplCustomer,
		productName,
		search,
		customerTable.currentPage,
		customerTable.pageSize,
		tableSorter.field,
		tableSorter.order,
		navigationContext.groupCompanyId,
	]);

	useEffect(() => {
		if (!isActive) {
			return undefined;
		}
		loadCustomerList();
		return undefined;
	}, [isActive, loadCustomerList]);

	useEffect(() => {
		if (!isActive) {
			return undefined;
		}
		setCustomerTable((prev) => ({ ...prev, currentPage: 1 }));
		return undefined;
	}, [search, isActive]);

	const handleTableAscDes = (pagination, _filters, sorter) => {
		const sorterList = Array.isArray(sorter) ? sorter : sorter ? [sorter] : [];
		const activeSorter =
			sorterList.find((item) => item && item.order) ||
			sorterList.find((item) => {
				const field = item?.field;
				const fieldStr = Array.isArray(field) ? field[0] : field;
				return fieldStr === tableSorter.field || item?.columnKey === tableSorter.field;
			}) ||
			sorterList[0];
		const rawField = activeSorter?.field;
		const sortField =
			rawField !== undefined && rawField !== null && rawField !== ""
				? Array.isArray(rawField)
					? rawField[0]
					: rawField
				: activeSorter?.columnKey ?? tableSorter.field;

		let nextOrder = tableSorter.order;
		if (activeSorter?.order === "ascend") {
			nextOrder = "ASC";
		} else if (activeSorter?.order === "descend") {
			nextOrder = "DESC";
		}

		setCustomerTable((prev) => ({
			...prev,
			currentPage: pagination?.current ?? prev.currentPage,
			pageSize: pagination?.pageSize ?? prev.pageSize,
		}));
		setTableSorter({
			field: sortField || tableSorter.field,
			order: nextOrder,
		});
	};

	const tableColumns = [
		{
			title: "Group Company",
			key: "groupCompany",
			sorter: true,
			showSorterTooltip: false,
			width: 220,
			sortOrder: getSortOrder(tableSorter, "groupCompany"),
			render: (_, record) => <span>{getCellValue(record, ["groupCompany"])}</span>,
		},
		{
			title: "Company Name",
			key: "accountName",
			sorter: true,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "accountName"),
			render: (_, record) => <span>{getCellValue(record, ["accountName"])}</span>,
		},
		{
			title: "Relation Type",
			key: "relation_Type",
			sorter: true,
			showSorterTooltip: false,
			width: 160,
			sortOrder: getSortOrder(tableSorter, "relation_Type"),
			render: (_, record) => <span>{getCellValue(record, ["relation_Type"])}</span>,
		},
		{
			title: "SAP Code",
			key: "sapCode",
			sorter: true,
			showSorterTooltip: false,
			width: 170,
			sortOrder: getSortOrder(tableSorter, "sapCode"),
			render: (_, record) => <span>{getCellValue(record, ["sapCode", "ispl_SAPCode"])}</span>,
		},
		{
			title: "Account Code",
			key: "ispl_AccountCode",
			sorter: true,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "ispl_AccountCode"),
			render: (_, record) => <span>{getCellValue(record, ["ispl_AccountCode"])}</span>,
		},
		{
			title: "Currency",
			key: "transactionCurrencyId",
			sorter: true,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "transactionCurrencyId"),
			render: (_, record) => <span>{getCellValue(record, ["transactionCurrencyId"])}</span>,
		},
		{
			title: "KPPL Division New",
			key: "ispl_KPPLDivisionNew",
			sorter: true,
			showSorterTooltip: false,
			width: 220,
			sortOrder: getSortOrder(tableSorter, "ispl_KPPLDivisionNew"),
			render: (_, record) => (
				<span>{getCellValue(record, ["ispl_KPPLDivisionNew", "KPPLDivisionNew"])}</span>
			),
		},
		{
			title: "Sales District",
			key: "ispl_SalesDistrict",
			sorter: true,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "ispl_SalesDistrict"),
			render: (_, record) => (
				<span>{getCellValue(record, ["ispl_SalesDistrict", "salesDistrict"])}</span>
			),
		},
		{
			title: "Sales Office",
			key: "ispl_SalesOffice",
			sorter: true,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "ispl_SalesOffice"),
			render: (_, record) => <span>{getCellValue(record, ["ispl_SalesOffice", "salesOffice"])}</span>,
		},
		{
			title: "Sales Type (Price Group)",
			dataIndex: "ispl_PriceGroupSalestype",
			key: "ispl_PriceGroupSalestype",
			sorter: true,
			showSorterTooltip: false,
			width: 140,
			sortOrder: getSortOrder(tableSorter, "ispl_PriceGroupSalestype"),
			render: (_, record) => (
				<span>{getCellValue(record, ["ispl_PriceGroupSalestype", "PriceGroupSalestype"])}</span>
			),
		},
		{
			title: "Distribution Channel",
			dataIndex: "ispl_DistributionChannel",
			key: "ispl_DistributionChannel",
			sorter: true,
			showSorterTooltip: false,
			width: 160,
			sortOrder: getSortOrder(tableSorter, "ispl_DistributionChannel"),
			render: (_, record) => (
				<span>{getCellValue(record, ["ispl_DistributionChannel", "distributionChannel"])}</span>
			),
		},
		{
			title: "Primary Contact",
			dataIndex: "primaryContactName",
			key: "primaryContactName",
			sorter: true,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "primaryContactName"),
			render: (_, record) => (
				<span>{record.primaryContact || record.primaryContactName || "-"}</span>
			),
		},
		{
			title: "Address 1",
			dataIndex: "address1_Line1",
			key: "address1_Line1",
			sorter: true,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "address1_Line1"),
			render: (_, record) => (
				<span>{getCellValue(record, ["address1_Line1", "address1Line1"])}</span>
			),
		},
		{
			title: "Address 2",
			dataIndex: "address1_Line2",
			key: "address1_Line2",
			sorter: true,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "address1_Line2"),
			render: (_, record) => (
				<span>{getCellValue(record, ["address1_Line2", "address1Line2"])}</span>
			),
		},
		{
			title: "Created On",
			dataIndex: "createdOn",
			key: "createdOn",
			sorter: true,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "createdOn"),
			render: (_, record) => <span>{formatKycCreatedOnDate(getCellValue(record, ["createdOn"]))}</span>,
		},
		{
			title: "Owner",
			dataIndex: "ownerid",
			key: "ownerid",
			sorter: true,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "ownerid"),
			render: (_, record) => <span>{getCellValue(record, ["ownerid"])}</span>,
		},
	];

	return (
		<Fragment>
			<Table
				bordered
				scroll={{ x: 2870 }}
				className="logistic_tables kyc_logistic_tables"
				dataSource={customerTable.rows}
				columns={tableColumns}
				sortDirections={["ascend", "descend"]}
				onChange={handleTableAscDes}
				rowKey={(record, index) =>
					record.id ||
					record.companyId ||
					record.accountId ||
					record.crmGuid ||
					record.ispl_AccountCode ||
					index
				}
				locale={{ emptyText: "No data found" }}
				pagination={{
					total: Number(customerTable.totalRecords || 0),
					current: Number(customerTable.currentPage || 1),
					pageSize: Number(customerTable.pageSize || 10),
					responsive: true,
					showSizeChanger: false,
					showTotal: formatRecordCount,
				}}
				loading={isLoading}
			/>
		</Fragment>
	);
};

export default KycMaterialCustomerTab;
